import React from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import type { WorkflowTemplate } from './WorkflowTemplatesDialog';

// 添加markdown样式组件
const MarkdownStyles = () => (
  <style>{`
    .markdown-content h1,
    .markdown-content h2,
    .markdown-content h3,
    .markdown-content h4,
    .markdown-content h5,
    .markdown-content h6 {
      color: #ffffff;
      margin-top: 1.5em;
      margin-bottom: 0.75em;
      font-weight: 600;
    }
    
    .markdown-content h1 {
      font-size: 1.875rem;
    }
    
    .markdown-content h2 {
      font-size: 1.5rem;
    }
    
    .markdown-content h3 {
      font-size: 1.25rem;
    }
    
    .markdown-content p {
      margin-bottom: 1em;
    }
    
    .markdown-content ul,
    .markdown-content ol {
      margin-bottom: 1em;
      padding-left: 1.5em;
    }
    
    .markdown-content li {
      margin-bottom: 0.5em;
    }
    
    .markdown-content a {
      color: #60a5fa;
      text-decoration: underline;
    }
    
    .markdown-content a:hover {
      color: #3b82f6;
    }
    
    .markdown-content code {
      background-color: rgba(107, 114, 128, 0.3);
      padding: 0.2em 0.4em;
      border-radius: 0.375rem;
      font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
      font-size: 0.875rem;
    }
    
    .markdown-content pre {
      background-color: rgba(31, 41, 55, 0.8);
      padding: 1em;
      border-radius: 0.375rem;
      overflow-x: auto;
      margin-bottom: 1em;
    }
    
    .markdown-content pre code {
      background-color: transparent;
      padding: 0;
    }
    
    .markdown-content blockquote {
      border-left: 4px solid #3b82f6;
      padding-left: 1em;
      margin-bottom: 1em;
      color: #9ca3af;
    }
    
    .markdown-content table {
      width: 100%;
      border-collapse: collapse;
      margin-bottom: 1em;
    }
    
    .markdown-content th,
    .markdown-content td {
      padding: 0.75em;
      border: 1px solid rgba(107, 114, 128, 0.3);
      text-align: left;
    }
    
    .markdown-content th {
      background-color: rgba(107, 114, 128, 0.2);
      font-weight: 600;
    }
  `}</style>
);

interface TemplateDetailViewProps {
  template: WorkflowTemplate;
  categories: Array<{ value: number; label: string }>;
  onBack: () => void;
  onImportTemplate: (template: WorkflowTemplate) => void;
  onClose: () => void;
}

const TemplateDetailView: React.FC<TemplateDetailViewProps> = ({
  template,
  categories,
  onBack,
  onImportTemplate,
  onClose
}) => {
  const { t } = useTranslation();

  const categoryName = categories.find(c => c.value === template.category)?.label || 'Unknown';

  // 添加调试代码，查看模板数据
  React.useEffect(() => {
    console.log('模板详情数据:', {
      name: template.name,
      description: template.description,
      descriptionType: typeof template.description,
      descriptionLength: template.description.length,
      // 截取前200个字符查看具体内容
      descriptionPreview: template.description.substring(0, 200) + (template.description.length > 200 ? '...' : '')
    });
  }, [template]);

  return (
    <div className="flex-1 overflow-y-auto p-4">
      <MarkdownStyles />
      {/* 返回按钮 */}
      <div className="mb-4">
        <button
          className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
          onClick={onBack}
        >
          <ArrowLeft size={16} />
          <span className="text-sm">{t('common.back')}</span>
        </button>
      </div>

      {/* 模板详情内容 */}
      <div className="space-y-8">
        {/* 预览图区域（放大） */}
        <div className="w-full rounded-lg overflow-hidden">
          {template.extras?.preview_url ? (
            <img
              src={template.extras.preview_url}
              alt={`${template.name} preview`}
              className="w-full h-auto object-contain"
            />
          ) : (
            <div className="w-full h-64 flex items-center justify-center">
              <span className="text-gray-400">{t('canvas.templates.dialog.preview')}</span>
            </div>
          )}
        </div>

        {/* 模板基本信息 */}
        <div className="space-y-4">
          <h2 className="text-2xl font-bold text-white">{template.name}</h2>
          <div className="text-gray-300 leading-relaxed markdown-content">
            <ReactMarkdown>{template.description}</ReactMarkdown>
          </div>

          {/* 分类和类型标签 */}
          <div className="flex flex-wrap gap-3">
            <span className="bg-gray-700/50 px-3 py-1.5 rounded-full text-sm text-gray-300">{categoryName}</span>
            <span className="bg-gray-700/50 px-3 py-1.5 rounded-full text-sm text-gray-300">{template.type}</span>
            <span className="bg-gray-700/50 px-3 py-1.5 rounded-full text-sm text-gray-300">{template.lang}</span>
          </div>
        </div>

        {/* 工作流详细信息 */}
        <div className="bg-gray-800/60 border border-gray-700 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-white mb-4">{t('canvas.templates.dialog.workflowSnippet')}</h3>

          <div className="space-y-4">
            {/* 工作流配置概览 */}
            <div className="flex flex-wrap gap-3">
              <span className="bg-blue-900/30 border border-blue-700/50 px-4 py-2 rounded-lg text-sm text-blue-300 font-medium">
                {template.config.nodes.length} {t('canvas.templates.dialog.nodes')}
              </span>
              <span className="bg-green-900/30 border border-green-700/50 px-4 py-2 rounded-lg text-sm text-green-300 font-medium">
                {template.config.edges.length} {t('canvas.templates.dialog.edges')}
              </span>
              {template.config.viewport && (
                <span className="bg-purple-900/30 border border-purple-700/50 px-4 py-2 rounded-lg text-sm text-purple-300 font-medium">
                  {t('canvas.templates.dialog.withViewport')}
                </span>
              )}
            </div>


          </div>
        </div>

        {/* 操作按钮 */}
        <div className="flex gap-4">
          <button
            className="flex-1 bg-blue-600 hover:bg-blue-500 text-white font-medium rounded-lg px-6 py-3 transition-all hover:shadow-md hover:shadow-blue-900/30"
            onClick={() => {
              onImportTemplate(template);
              onClose();
            }}
          >
            {t('canvas.templates.dialog.use')}
          </button>
          <button
            className="px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white font-medium rounded-lg transition-colors"
            onClick={onClose}
          >
            {t('common.close')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TemplateDetailView;